import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { CalendarDays, MapPin, Repeat } from "lucide-react";
import { colourOf, REPEAT_LABELS } from "@/components/calendar/palette";

export default function SharedCalendar() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, owner: "", events: [], error: "" });

  useEffect(() => {
    api
      .get(`/calendar/public/${token}`)
      .then(({ data }) => setState({ loading: false, owner: data.owner, events: data.events, error: "" }))
      .catch((e) =>
        setState({
          loading: false, owner: "", events: [],
          error: apiError(e.response?.data?.detail) || "That link is no longer live",
        }));
  }, [token]);

  const days = [...new Set(state.events.map((e) => e.day))];

  return (
    <div className="mx-auto max-w-2xl px-5 py-16 sm:px-8" data-testid="shared-calendar-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-brand">Shared calendar</p>
      <h1 className="mt-3 font-display text-[1.7rem] font-extrabold tracking-tighter sm:text-[2.25rem]">
        {state.loading ? "Opening…" : state.error ? "Nothing here" : `${state.owner}'s next few weeks`}
      </h1>

      {state.error && <p className="mt-3 text-sm text-ink-soft" data-testid="shared-calendar-error">{state.error}</p>}

      {!state.loading && !state.error && days.length === 0 && (
        <p className="mt-3 text-sm text-ink-soft">Nothing coming up on this calendar.</p>
      )}

      <div className="mt-6 space-y-3">
        {days.map((day) => (
          <div key={day} className="rounded-2xl border border-white/10 bg-panel p-4" data-testid={`shared-day-${day}`}>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-brand">
              {new Date(`${day}T12:00:00`).toLocaleDateString(undefined,
                { weekday: "long", day: "numeric", month: "long" })}
            </p>
            <div className="mt-2 space-y-1.5">
              {state.events.filter((e) => e.day === day).map((ev) => (
                <div key={`${ev.event_id}-${ev.day}`} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colourOf(ev.colour) }} />
                  <span className="w-24 text-xs tabular-nums text-ink-soft">
                    {ev.all_day ? "All day" : ev.start}
                  </span>
                  <span dir="auto" className="font-semibold">{ev.title}</span>
                  {ev.location && (
                    <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                      <MapPin className="h-3 w-3" /> {ev.location}
                    </span>
                  )}
                  {ev.repeat !== "none" && (
                    <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                      <Repeat className="h-3 w-3" /> {REPEAT_LABELS[ev.repeat]}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-8 inline-flex items-center gap-2 text-xs text-ink-soft">
        <CalendarDays className="h-3.5 w-3.5" /> Read only — shared from ChatMiLu.
      </p>
    </div>
  );
}
